import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import sanitizeHtml from "sanitize-html";
import Header from "../components/Header";
import Footer from "../components/Footer";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

const BlogsPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/blogs`);
        setBlogs(res.data || []);
      } catch (err) {
        console.error("Error fetching blogs:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  const slugify = (text = "") =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

  const getPreviewText = (html, limit = 160) => {
    if (!html) return "";

    // Remove headings completely
    let cleaned = html.replace(/<h[1-6][^>]*>.*?<\/h[1-6]>/gi, "");

    // Convert to plain text
    cleaned = sanitizeHtml(cleaned, { allowedTags: [] })
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    return cleaned.length > limit
      ? cleaned.substring(0, limit) + "..."
      : cleaned;
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      {/* HERO */}
      <section className="pt-24 sm:pt-28 md:pt-36 pb-10 px-4 bg-gradient-to-br from-green-50 to-blue-50 text-center">
        <div className="mx-auto w-full max-w-2xl">
          <div className="inline-block px-4 py-2 bg-green-100 text-green-700 text-xs sm:text-sm font-semibold rounded-full mb-3 sm:mb-4">
            Insights & Updates
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">
            Our Latest Blogs
          </h1>
          <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed px-2">
            Stay updated with expert insights, market trends, and practical tips
            from our team.
          </p>
        </div>
      </section>

      {/* BLOG CARDS */}
      <section className="py-10 sm:py-14 px-4 bg-white flex-1">
        <div className="mx-auto w-full max-w-6xl">
          {loading ? (
            <p className="text-center text-gray-500">Loading blogs...</p>
          ) : blogs.length === 0 ? (
            <p className="text-center text-gray-500 italic">
              No blogs available.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 justify-items-center">
              {blogs.map((blog) => (
                <div
                  key={blog.blog_id}
                  className="group bg-white w-full max-w-[360px] rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 flex flex-col overflow-hidden hover:-translate-y-2 hover:border-green-200"
                >
                  <div className="w-full aspect-video overflow-hidden">
                    <img
                      src={blog.blog_image}
                      alt={blog.blog_name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="p-5 sm:p-6 flex flex-col flex-1">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 line-clamp-2">
                      {blog.blog_name}
                    </h3>
                    <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-6 line-clamp-4 text-left">
                      {getPreviewText(blog.blog_description)}
                    </p>
                    <button
                      onClick={() =>
                       navigate(`/blogs/${slugify(blog.blog_name)}`)
                      }
                      className="mt-auto w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2 sm:py-3 rounded-xl text-sm sm:text-base transition-all duration-300"
                    >
                      Read More
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogsPage;
